class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // FF: Reduced debounce from 800ms to 500ms for better responsiveness
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      // FF: Listen to both 'input' and 'change' events for better checkbox support
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // FF: Reinitialize hover dwell effect on new product cards
    if (typeof window.FFInitHoverDwell === 'function') {
      window.FFInitHoverDwell(document.getElementById('ProductGridContainer'));
    }

    // FF: Reinitialize variant-aware hover images for filtered products
    if (typeof window.FFInitCardHoverImages === 'function') {
      window.FFInitCardHoverImages();
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    
    // FF: Update mobile Apply button count
    const applyCountEl = document.querySelector('.ff-apply-count');
    if (applyCountEl) {
      // Extract just the number from the count HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = count;
      const countText = tempDiv.textContent || tempDiv.innerText;
      // Parse out the number (handles formats like "123 products" or just "123")
      const match = countText.match(/\d+/);
      if (match) {
        applyCountEl.textContent = '(' + match[0] + ')';
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

        if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // Re-initialize our dual price sliders after filters re-render
    if (typeof ffInitPriceSliders === 'function') ffInitPriceSliders();
    if (typeof ffInitMobileFilterSortRow === 'function') ffInitMobileFilterSortRow();
    if (typeof ffInitStandaloneMobileBar === 'function') ffInitStandaloneMobileBar();
    if (typeof ffUpdateDesktopSidebarFade === 'function') ffUpdateDesktopSidebarFade();
    // FF: Re-initialize mobile drawer handlers after re-render
    if (typeof ffInitMobileCloseButton === 'function') ffInitMobileCloseButton();
    if (typeof ffInitMobileFilterForm === 'function') ffInitMobileFilterForm();
    if (typeof ffInitDropdownAnimations === 'function') ffInitDropdownAnimations();
  }

  static renderActiveFacets(html) {
  const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

  activeFacetElementSelectors.forEach((selector) => {
    const activeFacetsElement = html.querySelector(selector);
    if (!activeFacetsElement) return;
    const target = document.querySelector(selector);
    if (target) target.innerHTML = activeFacetsElement.innerHTML;
  });

  // FF: Update desktop active facets backdrop (conditionally rendered)
  const desktopBackdropNew = html.querySelector('.ff-desktop-active-backdrop');
  const desktopBackdropCurrent = document.querySelector('.ff-desktop-active-backdrop');
  const desktopActiveFacets = document.querySelector('.active-facets-desktop');
  
  if (desktopBackdropNew && desktopBackdropCurrent) {
    // Update existing backdrop
    desktopBackdropCurrent.outerHTML = desktopBackdropNew.outerHTML;
  } else if (!desktopBackdropNew && desktopBackdropCurrent) {
    // No active filters - remove backdrop
    desktopBackdropCurrent.remove();
  } else if (desktopBackdropNew && !desktopBackdropCurrent && desktopActiveFacets) {
    // Active filters exist but backdrop doesn't - insert after header
    const filterHeader = desktopActiveFacets.querySelector('.active-facets-vertical-filter');
    if (filterHeader) {
      filterHeader.insertAdjacentHTML('afterend', desktopBackdropNew.outerHTML);
    }
  }

  // FF: Update mobile drawer active facets (grouped structure)
  const mobileDrawerActiveFacets = html.querySelector('.ff-mobile-active-facets');
  const mobileDrawerTarget = document.querySelector('.ff-mobile-active-facets');
  const facetsWrapper = document.getElementById('FacetsWrapperMobile');
  
  if (mobileDrawerActiveFacets && mobileDrawerTarget) {
    // Update existing section
    mobileDrawerTarget.outerHTML = mobileDrawerActiveFacets.outerHTML;
  } else if (!mobileDrawerActiveFacets && mobileDrawerTarget) {
    // No active filters in new HTML, remove the section and divider
    const divider = mobileDrawerTarget.nextElementSibling;
    if (divider && divider.classList.contains('mobile-facets__divider')) {
      divider.remove();
    }
    mobileDrawerTarget.remove();
  } else if (mobileDrawerActiveFacets && !mobileDrawerTarget && facetsWrapper) {
    // Active filters exist but section doesn't - insert before first filter
    const firstFilter = facetsWrapper.querySelector('.mobile-facets__details--dropdown');
    if (firstFilter) {
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = mobileDrawerActiveFacets.outerHTML + '<div class="mobile-facets__divider" aria-hidden="true"></div>';
      firstFilter.insertAdjacentElement('beforebegin', tempContainer.firstElementChild);
      firstFilter.insertAdjacentElement('beforebegin', tempContainer.firstElementChild);
    }
  }

  // FF: Update filter count on mobile bar
  const countFromNew = html.querySelector('.ff-mobile-row__count, .ff-mobile-bar__count');
  const currentCount = document.querySelector('.ff-mobile-bar__count');
  
  if (countFromNew && currentCount) {
    currentCount.textContent = countFromNew.textContent;
  } else if (!countFromNew && currentCount) {
    currentCount.remove();
  } else if (countFromNew && !currentCount) {
    const filterLabel = document.querySelector('.ff-mobile-bar__btn[data-ff-role="filter"] .ff-mobile-bar__label');
    if (filterLabel) {
      const countSpan = document.createElement('span');
      countSpan.className = 'ff-mobile-bar__count';
      countSpan.textContent = countFromNew.textContent;
      filterLabel.appendChild(countSpan);
    }
  }

  FacetFiltersForm.toggleActiveFacets(false);
  
  // FF: Update scroll shadows after content changes
  if (typeof ffInitMobileScrollShadows === 'function') {
    setTimeout(ffInitMobileScrollShadows, 100);
  }
}

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (!target) return;
      target.innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm && mobileForm.closest('menu-drawer')) {
      mobileForm.closest('menu-drawer').bindEvents();
    }
    // No need to re-bind sort; we delegated at document level.
  }



  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const eventForm = event.target.closest('form');
    
    if (!eventForm) return;
    
    const isMobile = eventForm.id === 'FacetFiltersFormMobile';
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        // Desktop: collect from desktop forms
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
          forms.push(this.createSearchParams(form));
        }
      } else {
        // Mobile: collect from mobile form
        if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      }
    });
    
    if (forms.length > 0) {
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);
function ffInitPriceSliders() {
  const hosts = document.querySelectorAll('price-range');

  hosts.forEach((host) => {
    if (!host || host.dataset.ffPriceSliderInit === 'true') return;

    const slider = host.querySelector('[data-ff-price-slider]');
    const track = slider && slider.querySelector('.ff-price-slider__track');
    const rangeBar = slider && slider.querySelector('.ff-price-slider__range');
    const minThumb = slider && slider.querySelector('.ff-price-slider__thumb--min');
    const maxThumb = slider && slider.querySelector('.ff-price-slider__thumb--max');

    const minField = host.querySelector('input[id$="-GTE"]');
    const maxField = host.querySelector('input[id$="-LTE"]');

    if (!slider || !track || !rangeBar || !minThumb || !maxThumb || !minField || !maxField) {
      return;
    }

    const parseNumber = (value) => {
      if (!value) return 0;
      const cleaned = String(value).replace(/[^0-9.]/g, '');
      return cleaned === '' ? 0 : Number(cleaned);
    };

    const sliderMaxAttr =
      parseNumber(maxField.getAttribute('data-max')) ||
      parseNumber(maxField.placeholder) ||
      0;

    const sliderMin = 0;
    const sliderMax = sliderMaxAttr || 0;

    if (!sliderMax || sliderMax <= sliderMin) {
      // Nothing sensible to slide against; bail gracefully.
      return;
    }

    const clamp = (value) => {
      if (Number.isNaN(value)) value = 0;
      return Math.min(sliderMax, Math.max(sliderMin, value));
    };
    // Slider uses whole currency units – no crazy decimals
    const step = 1;

    const roundToStep = (value) => {
      const clamped = clamp(value);
      if (!step || step <= 0) return clamped;
      return Math.round(clamped / step) * step;
    };

    const valueToPercent = (value) => {
      const clamped = clamp(value);
      const span = sliderMax - sliderMin;
      if (!span) return 0;
      return ((clamped - sliderMin) / span) * 100;
    };

    const syncThumbsFromFields = () => {
      let minVal = parseNumber(minField.value);
      let maxVal = parseNumber(maxField.value);

      if (!maxVal) maxVal = sliderMax;
      if (minVal < sliderMin) minVal = sliderMin;
      if (maxVal > sliderMax) maxVal = sliderMax;
      if (minVal > maxVal) minVal = maxVal;

      const minPct = valueToPercent(minVal);
      const maxPct = valueToPercent(maxVal);

      minThumb.style.left = `${minPct}%`;
      maxThumb.style.left = `${maxPct}%`;

      rangeBar.style.left = `${minPct}%`;
      rangeBar.style.width = `${maxPct - minPct}%`;
    };

       const applyValues = (nextMin, nextMax, { dispatch = false } = {}) => {
      const minVal = roundToStep(nextMin);
      const maxVal = roundToStep(nextMax < minVal ? minVal : nextMax);

      minField.value = minVal ? String(minVal) : '';
      maxField.value = maxVal ? String(maxVal) : '';

      syncThumbsFromFields();

      if (dispatch) {
        const inputEvent = new Event('input', { bubbles: true });
        maxField.dispatchEvent(inputEvent);
      }
    };

    // Text field → slider sync
    minField.addEventListener('input', () => {
      let minVal = parseNumber(minField.value);
      let maxVal = parseNumber(maxField.value) || sliderMax;
      if (minVal < sliderMin) minVal = sliderMin;
      if (minVal > maxVal) minVal = maxVal;
      applyValues(minVal, maxVal);
    });

    maxField.addEventListener('input', () => {
      let maxVal = parseNumber(maxField.value);
      let minVal = parseNumber(minField.value);
      if (!minVal) minVal = sliderMin;
      if (maxVal > sliderMax) maxVal = sliderMax;
      if (maxVal < minVal) maxVal = minVal;
      applyValues(minVal, maxVal);
    });

    let activeHandle = null;

    const pointerToValue = (clientX) => {
      const rect = track.getBoundingClientRect();
      if (!rect.width) return sliderMin;
      const ratio = (clientX - rect.left) / rect.width;
      const clampedRatio = Math.min(1, Math.max(0, ratio));
      return sliderMin + clampedRatio * (sliderMax - sliderMin);
    };

    const handlePointerMove = (event) => {
      if (!activeHandle) return;

      const pointerValue = pointerToValue(event.clientX);
      let minVal = parseNumber(minField.value) || sliderMin;
      let maxVal = parseNumber(maxField.value) || sliderMax;

      if (activeHandle === 'min') {
        minVal = Math.min(pointerValue, maxVal);
      } else {
        maxVal = Math.max(pointerValue, minVal);
      }

      applyValues(minVal, maxVal);
    };

    const handlePointerUp = () => {
      if (!activeHandle) return;
      activeHandle = null;

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);

      const inputEvent = new Event('input', { bubbles: true });
      maxField.dispatchEvent(inputEvent);
    };

    const handlePointerDown = (event) => {
      if (event.button !== 0) return;

      event.preventDefault();

      const pointerValue = pointerToValue(event.clientX);
      let minVal = parseNumber(minField.value) || sliderMin;
      let maxVal = parseNumber(maxField.value) || sliderMax;

      if (event.target === minThumb) {
        activeHandle = 'min';
      } else if (event.target === maxThumb) {
        activeHandle = 'max';
      } else {
        const distToMin = Math.abs(pointerValue - minVal);
        const distToMax = Math.abs(pointerValue - maxVal);
        activeHandle = distToMin <= distToMax ? 'min' : 'max';
      }

      if (activeHandle === 'min') {
        minVal = Math.min(pointerValue, maxVal);
      } else {
        maxVal = Math.max(pointerValue, minVal);
      }

      applyValues(minVal, maxVal);

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    };

    track.addEventListener('pointerdown', handlePointerDown);
    
    // FF: Add touch support for track clicks on mobile
    track.addEventListener('touchstart', function(event) {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const syntheticEvent = {
        clientX: touch.clientX,
        button: 0,
        target: event.target,
        preventDefault: function() { 
          if (event.cancelable) event.preventDefault(); 
        }
      };
      handlePointerDown(syntheticEvent);
    }, { passive: false });
    minThumb.addEventListener('pointerdown', handlePointerDown);
    maxThumb.addEventListener('pointerdown', handlePointerDown);

    syncThumbsFromFields();
    host.dataset.ffPriceSliderInit = 'true';
  });
}
function ffUpdateDesktopSidebarFade() {
  const aside = document.querySelector('#main-collection-filters.facets-wrapper');
  if (!aside) return;

  const scroller = aside.querySelector('.facets-container');
  if (!scroller) return;

  // Small tolerance so a 1px rounding difference doesn't trigger the fade
  const hasScroll = scroller.scrollHeight - scroller.clientHeight > 4;
  if (hasScroll) {
    aside.classList.add('ff-has-scroll');
  } else {
    aside.classList.remove('ff-has-scroll');
  }
}

function ffCollapseAllFacetsOnLoad() {
  const filtersRoot = document.querySelector('#main-collection-filters');
  if (!filtersRoot) return;

  const flag = filtersRoot.dataset.ffCollapseOnLoad;
  // If setting is explicitly false/0, do nothing
  if (flag === 'false' || flag === '0') return;

  filtersRoot.querySelectorAll('details[open]').forEach((details) => {
    details.removeAttribute('open');
  });
}

/**
 * Mobile filter/sort row:
 * The old ffMobileFilterState/scroll logic has been replaced by the standalone
 * .ff-mobile-bar implementation. We keep this tiny stub so any guarded calls
 * (typeof ffInitMobileFilterSortRow === 'function') continue to work safely.
 */
function ffInitMobileFilterSortRow() {
  // No-op: mobile behaviour is handled by ffInitStandaloneMobileBar now.
}

function ffFindHeaderEl() {
  return (
    document.querySelector('sticky-header') ||
    document.getElementById('shopify-section-header') ||
    document.querySelector('[id^="shopify-section-"][id*="header" i]') ||
    document.querySelector('header.header') ||
    document.querySelector('header')
  );
}

function ffGetHeaderBottomPx() {
  const el = ffFindHeaderEl();
  if (el) return Math.ceil(el.getBoundingClientRect().bottom);
  // Fallback to global CSS var if present
  const cssVal = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
  const n = parseFloat(cssVal);
  return Number.isFinite(n) ? n : 72;
}

function ffOpenMobileFacetsDrawer(options = {}) {
  const wrapper = document.querySelector('.mobile-facets__wrapper');
  if (!wrapper) return;

  const details = wrapper.querySelector('.mobile-facets__disclosure');
  if (!details) return;

  const summary = details.querySelector('.mobile-facets__open-wrapper');
  if (!summary) return;

  const host = details.closest('[id^="shopify-section-"]') || document.body;
  const top = ffGetHeaderBottomPx();

  // Set CSS variable for any CSS that needs it
  host.style.setProperty('--ff-mobile-facets-top', top + 'px');

  // ALSO set inline style directly on the overlay so it's always correct
  const overlay = details.querySelector('.ff-mobile-facets-overlay');
  if (overlay) {
    overlay.style.top = top + 'px';
  }

  // Set on the drawer panel too so it starts below header
  const panel = details.querySelector('.mobile-facets__inner');
  if (panel) {
    panel.style.top = top + 'px';
    panel.style.height = 'calc(100vh - ' + top + 'px)';
    panel.style.height = 'calc(100dvh - ' + top + 'px)';
  }

  const isOpen = details.hasAttribute('open');

  if (options.ensureOpen) {
    if (!isOpen) {
      summary.click();
    }
  } else {
    summary.click();
  }
}

function ffInitStandaloneMobileBar() {
  try {
    const bar = document.querySelector('.ff-mobile-bar[data-ff-mobile-bar]');
    if (!bar) return;

    const host = bar.closest('[id^="shopify-section-"]') || document.body;

    // Keep offset vars aligned with sticky header bottom
    const applyPositions = () => {
      const bottom = ffGetHeaderBottomPx();
      host.style.setProperty('--ff-mobile-row-top', bottom + 'px');
      host.style.setProperty('--ff-mobile-facets-top', bottom + 'px');

      // Also pre-set drawer panel position for when it opens
      const panel = host.querySelector('.mobile-facets__inner');
      if (panel) {
        panel.style.top = bottom + 'px';
        panel.style.height = 'calc(100dvh - ' + bottom + 'px)';
      }

      const overlay = host.querySelector('.ff-mobile-facets-overlay');
      if (overlay) {
        overlay.style.top = bottom + 'px';
      }
    };

    applyPositions();
    let raf = null;
    const onSR = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        applyPositions();
        raf = null;
      });
    };
    window.addEventListener('scroll', onSR, { passive: true });
    window.addEventListener('resize', onSR);

    // FILTER: open the drawer directly without relying on hidden summary click
    const btnFilter = bar.querySelector('[data-ff-role="filter"]');
    if (btnFilter && !btnFilter.dataset.ffBound) {
      btnFilter.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Update positions immediately before opening
        applyPositions();
        
        const wrapper = host.querySelector('.mobile-facets__wrapper');
        if (!wrapper) return;
        
        const details = wrapper.querySelector('.mobile-facets__disclosure');
        if (!details) return;
        
        const isCurrentlyOpen = details.classList.contains('menu-opening');
        
        if (isCurrentlyOpen) {
          // CLOSE: remove class first, then remove [open] after transition
          details.classList.remove('menu-opening');
          document.body.classList.remove('overflow-hidden-mobile');
          setTimeout(function() {
            details.removeAttribute('open');
          }, 300);
        } else {
          // OPEN: Move panel to body FIRST (before any details/class changes)
          // This prevents native details[open] from flashing the content
          if (typeof window.ffOpenMobileDrawer === 'function') {
            window.ffOpenMobileDrawer();
          }
          
          // Now set details state for tracking (panel already moved, so no flash)
          details.setAttribute('open', '');
          details.classList.add('menu-opening');
          document.body.classList.add('overflow-hidden-mobile');
        }
      }, false);
      btnFilter.dataset.ffBound = '1';
    }

    // SORT: proxy <select> that opens the OS-native picker and syncs to the real sort
    const proxySelect = bar.querySelector('[data-ff-mobile-sort-proxy]');
    if (proxySelect && !proxySelect.dataset.ffBound) {
      const getRealSelect = () =>
        document.getElementById('SortBy-mobile') || document.getElementById('SortBy');

      // On init, mirror current real sort into proxy (if available)
      const syncFromReal = () => {
        const real = getRealSelect();
        if (!real) return;
        proxySelect.value = real.value;
      };

      const syncToReal = () => {
        const real = getRealSelect();
        if (!real) return;

        real.value = proxySelect.value;

        // Trigger FacetFiltersForm AJAX reload via input event
        const ev = new Event('input', { bubbles: true });
        real.dispatchEvent(ev);
      };

      proxySelect.addEventListener('change', syncToReal);

      // Initial sync
      syncFromReal();

      proxySelect.dataset.ffBound = '1';
    }
  } catch (err) {
    console.warn('ffInitStandaloneMobileBar error:', err);
  }
}

function ffHandlePageRestore() {
  // Called on pageshow to handle bfcache restoration
  const bar = document.querySelector('.ff-mobile-bar[data-ff-mobile-bar]');
  if (!bar) return;
  
  const host = bar.closest('[id^="shopify-section-"]') || document.body;
  const details = host.querySelector('.mobile-facets__disclosure');
  
  if (!details) return;
  
  // Update CSS variables immediately
  const bottom = ffGetHeaderBottomPx();
  host.style.setProperty('--ff-mobile-row-top', bottom + 'px');
  host.style.setProperty('--ff-mobile-facets-top', bottom + 'px');
  
  const panel = host.querySelector('.mobile-facets__inner');
  if (panel) {
    panel.style.top = bottom + 'px';
    panel.style.height = 'calc(100dvh - ' + bottom + 'px)';
  }
  
  const overlay = host.querySelector('.ff-mobile-facets-overlay');
  if (overlay) {
    overlay.style.top = bottom + 'px';
  }
  
  // Clean up stale drawer state
  const isOpen = details.hasAttribute('open');
  const hasMenuOpening = details.classList.contains('menu-opening');
  
  if (isOpen && !hasMenuOpening) {
    // Stale state from bfcache - drawer was "open" but not properly initialized
    details.removeAttribute('open');
    document.body.classList.remove('overflow-hidden-mobile');
    document.body.classList.remove('overflow-hidden-tablet');
    document.body.classList.remove('overflow-hidden');
  }
  
  // Force re-bind the filter button by clearing the flag
  const btnFilter = bar.querySelector('[data-ff-role="filter"]');
  if (btnFilter) {
    delete btnFilter.dataset.ffBound;
  }
  
  // Re-initialize
  ffInitStandaloneMobileBar();
}

function ffInitMobileFacetsOverlayClick() {
  // For each section that has our standalone mobile bar
  document.querySelectorAll('[data-ff-mobile-bar]').forEach(function(bar) {
    var section = bar.closest('[id^="shopify-section-"]');
    if (!section) return;

    var wrapper = section.querySelector('.mobile-facets__wrapper');
    if (!wrapper) return;

    var disclosure = wrapper.querySelector('.mobile-facets__disclosure');
    if (!disclosure) return;

    // FF FIX: Select the actual overlay element, not the form
    var overlay = disclosure.querySelector('.ff-mobile-facets-overlay');
    var panel   = disclosure.querySelector('.mobile-facets__inner');    // sliding drawer
    var summary = disclosure.querySelector('.mobile-facets__open-wrapper'); // the <summary>

    if (!overlay || !panel || !summary) return;

    // Avoid binding multiple times on the same overlay
    if (overlay.dataset.ffDimBound === 'true') return;
    overlay.dataset.ffDimBound = 'true';

    overlay.addEventListener('click', function(event) {
      // Clicked on the dimmed area → close the drawer
      event.preventDefault();
      event.stopPropagation();

      // Use a synthetic click on the <summary> so Dawn's own logic
      // runs (details toggle + scroll-lock handling, etc.)
      summary.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
      );
    });
  });
}

// FF: Initialize close button - calls closeMenuDrawer properly
function ffInitMobileCloseButton() {
  // Close buttons now use inline onclick - no JS needed
}

// FF: Re-bind events for mobile filter form (let Dawn handle the actual filtering)
function ffInitMobileFilterForm() {
  if (document._ffMobileFilterReady) return;
  document._ffMobileFilterReady = true;
  
  // Helper to submit the mobile form
  function submitMobileForm() {
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;
    
    const params = new URLSearchParams();
    
    // 1. Inputs physically inside the form
    form.querySelectorAll('input, select').forEach(function(input) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) params.append(input.name, input.value);
      } else if (input.tagName === 'SELECT') {
        params.append(input.name, input.value);
      } else if (input.type === 'text' && input.value) {
        params.append(input.name, input.value);
      }
    });
    
    // 2. Inputs with form="FacetFiltersFormMobile" (may be outside form due to drawer move)
    document.querySelectorAll('[form="FacetFiltersFormMobile"]').forEach(function(input) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) {
          const existing = params.getAll(input.name);
          if (!existing.includes(input.value)) {
            params.append(input.name, input.value);
          }
        }
      } else if (input.type === 'text' && input.value) {
        // For price inputs, avoid duplicates
        if (!params.has(input.name)) {
          params.append(input.name, input.value);
        }
      } else if (input.tagName === 'SELECT') {
        if (!params.has(input.name)) {
          params.append(input.name, input.value);
        }
      }
    });
    
    const searchParams = params.toString();
    
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(searchParams, null);
    }
  }
  
  // Listen for change events (checkboxes, selects)
  document.addEventListener('change', function(e) {
    const target = e.target;
    const formAttr = target.getAttribute('form');
    const parentForm = target.closest('form');
    
    const isMobileFormInput = 
      formAttr === 'FacetFiltersFormMobile' || 
      (parentForm && parentForm.id === 'FacetFiltersFormMobile');
    
    if (!isMobileFormInput) return;
    if (target.type !== 'checkbox' && target.type !== 'radio') return;
    
    setTimeout(submitMobileForm, 50);
  });
  
  // Listen for input events (price text fields from slider)
  document.addEventListener('input', function(e) {
    const target = e.target;
    const formAttr = target.getAttribute('form');
    const parentForm = target.closest('form');
    
    const isMobileFormInput = 
      formAttr === 'FacetFiltersFormMobile' || 
      (parentForm && parentForm.id === 'FacetFiltersFormMobile');
    
    if (!isMobileFormInput) return;
    
    // Only handle text inputs (price fields)
    if (target.type !== 'text') return;
    
    // Check if it's a price field (ends with -GTE or -LTE)
    if (!target.id || (!target.id.endsWith('-GTE') && !target.id.endsWith('-LTE'))) return;
    
    
    // Debounce price inputs
    clearTimeout(target._ffDebounce);
    target._ffDebounce = setTimeout(submitMobileForm, 500);
  });
}

function ffInitDropdownAnimations() {
  // Animation is now handled purely by CSS
  // This function kept for compatibility but does nothing
  // CSS rule: .mobile-facets__details--dropdown[open] .mobile-facets__dropdown-content
  // has animation: ffMobileDropdown 0.2s ease-out forwards;
}

// FF: Adjust drawer for dynamic browser UI (Chrome tab bar, etc.)
function ffInitMobileBrowserUIOffset() {
  // This is now handled in the inline script in main-collection-product-grid.liquid
  // which updates drawer height based on visualViewport
}

// FF: Mobile drawer scroll shadows
function ffInitMobileScrollShadows() {
  const scrollArea = document.getElementById('FacetsWrapperMobile');
  const header = document.querySelector('.ff-mobile-header');
  const footer = document.querySelector('.ff-mobile-footer');
  
  if (!scrollArea || !header || !footer) return;
  
  function updateScrollShadows() {
    const scrollTop = scrollArea.scrollTop;
    const scrollHeight = scrollArea.scrollHeight;
    const clientHeight = scrollArea.clientHeight;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    const isScrollable = scrollHeight > clientHeight + 2; // 2px tolerance
    
    // Show header shadow if scrolled down more than 2px
    if (isScrollable && scrollTop > 2) {
      header.classList.add('has-scroll-shadow');
    } else {
      header.classList.remove('has-scroll-shadow');
    }
    
    // Show footer shadow if more content hidden below (and scrollable)
    if (isScrollable && scrollBottom > 2) {
      footer.classList.add('has-scroll-shadow');
    } else {
      footer.classList.remove('has-scroll-shadow');
    }
  }
  
  // Avoid binding multiple times
  if (scrollArea._ffScrollShadowsBound) {
    updateScrollShadows();
    return;
  }
  scrollArea._ffScrollShadowsBound = true;
  
  // Listen for scroll events
  scrollArea.addEventListener('scroll', updateScrollShadows, { passive: true });
  
  // Use ResizeObserver to detect content size changes (including during animation)
  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => {
      updateScrollShadows();
    });
    resizeObserver.observe(scrollArea);
    
    // Also observe children that might change size
    scrollArea.querySelectorAll('.mobile-facets__details--dropdown').forEach(el => {
      resizeObserver.observe(el);
    });
  }
  
  // Listen for accordion animation completion (dispatched by ff-details-animate.js)
  scrollArea.addEventListener('ff:accordion-settled', function() {
    updateScrollShadows();
  });
  
  // Also check when drawer opens
  const drawerDetails = document.querySelector('.mobile-facets__disclosure');
  if (drawerDetails && !drawerDetails._ffScrollShadowToggleBound) {
    drawerDetails._ffScrollShadowToggleBound = true;
    drawerDetails.addEventListener('toggle', function() {
      if (drawerDetails.open) {
        setTimeout(updateScrollShadows, 50);
        setTimeout(updateScrollShadows, 200);
      } else {
        // Reset shadows when drawer closes
        header.classList.remove('has-scroll-shadow');
        footer.classList.remove('has-scroll-shadow');
      }
    });
  }
  
  // Check on window resize
  if (!window._ffScrollShadowResizeBound) {
    window._ffScrollShadowResizeBound = true;
    window.addEventListener('resize', updateScrollShadows, { passive: true });
  }
  
  // Initial check
  updateScrollShadows();
}

document.addEventListener('DOMContentLoaded', () => {
  ffInitPriceSliders();
  ffCollapseAllFacetsOnLoad();
  ffInitMobileFilterSortRow();
  ffInitStandaloneMobileBar();       // must be here
  ffInitMobileFacetsOverlayClick();  // dim click → close
  ffInitMobileCloseButton();         // Close button click
  ffInitMobileFilterForm();          // Explicit mobile filter handling
  ffInitDropdownAnimations();        // Dropdown open animations
  ffUpdateDesktopSidebarFade();
  ffInitMobileScrollShadows();       // Mobile drawer scroll shadows
  ffInitMobileBrowserUIOffset();     // Browser UI offset (Chrome tab bar, etc.)
});

document.addEventListener('shopify:section:load', () => {
  ffInitMobileFilterSortRow();
  ffInitStandaloneMobileBar();       // must be here
  ffInitMobileFacetsOverlayClick();
  ffInitMobileCloseButton();
  ffInitMobileFilterForm();
  ffInitDropdownAnimations();
  ffInitMobileScrollShadows();
  ffInitMobileBrowserUIOffset();
});

document.addEventListener('shopify:section:select', () => {
  ffInitMobileFilterSortRow();
  ffInitStandaloneMobileBar();       // must be here
  ffInitMobileFacetsOverlayClick();
  ffInitMobileCloseButton();
  ffInitMobileFilterForm();
  ffInitDropdownAnimations();
});

document.addEventListener('shopify:section:reorder', () => {
  ffInitMobileFilterSortRow();
  ffInitStandaloneMobileBar();       // must be here
  ffInitMobileFacetsOverlayClick();
  ffInitMobileCloseButton();
  ffInitMobileFilterForm();
  ffInitDropdownAnimations();
});

// Add this right after the existing DOMContentLoaded listener
window.addEventListener('pageshow', function(event) {
  // Always handle page restore, whether from bfcache or not
  ffHandlePageRestore();
  
  // Also run with slight delays to catch late-loading elements
  setTimeout(ffHandlePageRestore, 50);
  setTimeout(ffHandlePageRestore, 150);
});

// FF: Handle facet removal via AJAX instead of page refresh
// This uses event delegation so it works for dynamically added elements
document.addEventListener('click', function(event) {
  // Find if click was on a facet removal link
  // Desktop: links inside facet-remove elements
  // Mobile: .ff-mobile-active-facets__item links or .ff-mobile-active-facets__clear
  const link = event.target.closest('facet-remove a, facet-remove button, a.ff-mobile-active-facets__item, a.ff-mobile-active-facets__clear');
  if (!link) return;
  
  // Only handle links with href (not buttons without href)
  const href = link.getAttribute('href');
  if (!href) return;
  
  // Prevent default navigation
  event.preventDefault();
  
  // Extract search params from the href
  const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
  
  // Disable active facets during update
  FacetFiltersForm.toggleActiveFacets();
  
  // Use AJAX to update the page
  FacetFiltersForm.renderPage(url);
  
  // Keep mobile drawer open if we're inside it
  const drawer = link.closest('.mobile-facets__wrapper, #FacetsWrapperMobile');
  if (drawer) {
    // The drawer might try to close, so we'll ensure it stays open
    const menuDrawer = document.querySelector('menu-drawer.mobile-facets__wrapper');
    if (menuDrawer && menuDrawer.openMenuDrawer) {
      // Small delay to let the AJAX complete, then ensure drawer stays open
      setTimeout(() => {
        const details = menuDrawer.querySelector('details');
        if (details && !details.open) {
          details.open = true;
        }
      }, 100);
    }
  }
});